/**
 * Select Component
 * Native HTML select with styling
 */

import React, { forwardRef } from 'react';
import styled from '@emotion/styled';
import { transformStyleProps, splitProps, stylePropsKeys } from '../system/style-props';
import { useTheme } from '../theme';
import { Box } from '../primitives/Box';

const StyledSelect = styled('select', {
  shouldForwardProp: (prop) => {
    if (stylePropsKeys.includes(prop)) return false;
    if (['as', 'sx', '__css', 'variant', 'size', 'isDisabled', 
         'isInvalid', 'isReadOnly', 'isRequired', 'icon',
         'iconColor', 'iconSize', 'rootProps'].includes(prop)) return false;
    return true;
  },
})(({ theme, ...props }) => {
  const styles = [];

  if (props.__css) {
    const cssResult = typeof props.__css === 'function'
      ? props.__css({ theme, colorMode: 'light', ...props })
      : props.__css;
    styles.push(transformStyleProps(cssResult, theme));
  }

  const [styleProps] = splitProps(props);
  styles.push(transformStyleProps(styleProps, theme));

  if (props.sx) {
    const sxResult = typeof props.sx === 'function'
      ? props.sx({ theme, ...props })
      : props.sx;
    styles.push(transformStyleProps(sxResult, theme));
  }

  return styles;
});

/**
 * Select Component
 * 
 * @example
 * <Select placeholder="Select option" variant="main">
 *   <option value="1">Option 1</option>
 *   <option value="2">Option 2</option>
 * </Select>
 */
export const Select = forwardRef(({
  as,
  variant = 'outline',
  size = 'md',
  isDisabled = false,
  isInvalid = false,
  isReadOnly = false,
  isRequired = false,
  placeholder,
  icon = <></>,
  iconColor,
  iconSize = '24px',
  rootProps,
  children,
  ...props
}, ref) => {
  const theme = useTheme();
  const selectStyles = theme?.components?.Select || {};
  
  const baseStyle = selectStyles.baseStyle?.field || {};
  const sizeStyle = selectStyles.sizes?.[size]?.field || {};
  const variantStyle = typeof selectStyles.variants?.[variant] === 'function'
    ? (selectStyles.variants[variant]({ colorMode: 'light' })?.field || {})
    : (selectStyles.variants?.[variant]?.field || {});

  const mergedStyles = {
    width: '100%',
    minWidth: 0,
    outline: 0,
    position: 'relative',
    appearance: 'none',
    transition: 'all 0.2s',
    paddingRight: '2rem',
    ...baseStyle,
    ...sizeStyle,
    ...variantStyle,
    ...(isDisabled ? { opacity: 0.4, cursor: 'not-allowed' } : {}),
    ...(isInvalid ? { borderColor: 'red.500' } : {}),
  };

  const cleanStyles = { ...mergedStyles };
  ['_hover', '_focus', '_disabled', '_invalid', '_placeholder'].forEach(key => {
    delete cleanStyles[key];
  });

  return (
    <Box position="relative" width="100%" {...rootProps}>
      <StyledSelect
        ref={ref}
        as={as}
        disabled={isDisabled}
        readOnly={isReadOnly}
        required={isRequired}
        aria-invalid={isInvalid}
        aria-required={isRequired}
        data-invalid={isInvalid || undefined}
        __css={cleanStyles}
        {...props}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {children}
      </StyledSelect>
      {icon && (
        <Box
          paddingRight="0.5rem"
          position="absolute"
          top="50%"
          right="0.5rem"
          transform="translateY(-50%)"
          pointerEvents="none"
          color={iconColor || mergedStyles.color}
          fontSize={iconSize}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          {icon}
        </Box>
      )}
    </Box>
  );
});

Select.displayName = 'Select';

/**
 * Default select icon (chevron down)
 */
const SelectIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="1em"
    height="1em"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

export default Select;
